import type { TFunction } from 'i18next';
import {
  formatStatesOf,
  STATEMENT_FORMATS,
  type PeriodSource,
  type Readiness,
  type StatementArchive,
  type StatementFormat,
} from './prima-nota-model';

/** One line of the Drive panel: an account's format, or the one statement of an older server. */
export type ArchiveRow = {
  accountKey: string;
  accountLabel: string;
  format: StatementFormat | null;
  archive: StatementArchive;
};

/** One upload slot for a statement: a format an account is missing, or its one statement. */
export type StatementSlot = {
  accountKey: string;
  accountLabel: string;
  format: StatementFormat | null;
};

/** The key of one upload slot, which is also the busy marker while it uploads. */
export function slotKey(
  kind: PeriodSource['kind'],
  accountKey: string | null,
  format: StatementFormat | null,
): string {
  return `${kind}:${accountKey ?? ''}:${format ?? ''}`;
}

/** A format as the operator names it, in every language: the file types' own names. */
export function formatName(format: StatementFormat): string {
  return format === 'pdf' ? 'PDF' : 'CSV';
}

// Each format of each account on its own line and in its own slot, the PDF before the CSV
// (decision 71). A server that predates the two formats knew one statement per account, and its
// accounts are shown as they always were: one line, one slot.
export function buildArchiveRows(statements: Readiness['statements']): ArchiveRow[] {
  return statements.flatMap<ArchiveRow>((statement) => {
    const account = { accountKey: statement.accountKey, accountLabel: statement.accountLabel };
    const formats = formatStatesOf(statement);
    if (formats === null) {
      return statement.archive == null
        ? []
        : [{ ...account, format: null, archive: statement.archive }];
    }
    return STATEMENT_FORMATS.flatMap((format) => {
      const archive = formats[format].archive;
      return archive === null ? [] : [{ ...account, format, archive }];
    });
  });
}

export function buildStatementSlots(statements: Readiness['statements']): StatementSlot[] {
  return statements.flatMap<StatementSlot>((statement) => {
    const account = { accountKey: statement.accountKey, accountLabel: statement.accountLabel };
    const formats = formatStatesOf(statement);
    if (formats === null) {
      return statement.supplied ? [] : [{ ...account, format: null }];
    }
    return STATEMENT_FORMATS.filter((format) => !formats[format].supplied).map((format) => ({
      ...account,
      format,
    }));
  });
}

/**
 * One format of one account's archive state, in a sentence. "Nobody looked" and "not found" are
 * different sentences on purpose, and "not found" says whether that is early or late.
 */
export function archiveLine(
  archive: StatementArchive | null | undefined,
  t: TFunction<'finance'>,
  booksDay: Intl.DateTimeFormat,
): string {
  const sweep = archive?.lastSweep ?? null;
  if (archive === undefined || archive === null || sweep === null) {
    return t('primaNota.archive.notSwept');
  }
  const date = booksDay.format(new Date(sweep.sweptAt));
  // Noon UTC is the same calendar day in Rome, so a day written as a date never shifts.
  const due = booksDay.format(new Date(`${archive.dueFrom}T12:00:00Z`));
  switch (sweep.outcome) {
    case 'imported':
      return t('primaNota.archive.imported', { date, file: sweep.fileNames[0] ?? '' });
    case 'already-held':
      // The file was already among the period's files — by an earlier sweep or by hand. The date
      // is the check's, not the day it arrived.
      return t('primaNota.archive.alreadyHeld', { date, file: sweep.fileNames[0] ?? '' });
    case 'not-found':
      return archive.lateness === 'late'
        ? t('primaNota.archive.late', { due, date })
        : t('primaNota.archive.notYetExpected', { due, date });
    default:
      return t('primaNota.archive.problem', { date, detail: sweep.detail });
  }
}
